import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMediaQuery, Box, useTheme } from '@mui/material';
import ChatSidebar from '../components/Chat/ChatSidebar';
import ChatConversation from '../components/Chat/ChatConversation';
import { Session, Language, ProfessionType, CommunicationChannel, QuestionType } from '../models/session';
import { MessageSenderType } from '../models/message';
import { getSessionByWorkerId, getSessions, markSessionAsViewed } from '../api/chatApi';
import logger from '../utils/logger';
import socketService from "../services/socketService";
import {
    hasValidSessionAuthoritySnapshot,
    isValidSessionHttpSnapshot,
    mergeSessionListSocketUpdate,
    mergeSessionSocketUpdate,
    readSessionAuthorityVersion,
    replaySessionSocketUpdates,
    type SessionSocketReplayUpdate,
} from '../services/sessionAuthority';
import { useLocation, useNavigate } from 'react-router-dom';

let listUpdateEventCounter = 0;
const SESSION_RECONCILIATION_TIMEOUT_MS = 5_000;
const SESSION_RECONCILIATION_EVENT_LIMIT = 256;

const diagnosticErrorType = (error: unknown): string => {
    const name = error instanceof Error ? error.name : '';
    return /^[A-Za-z][A-Za-z0-9]{0,63}$/.test(name) ? name : 'Error';
};

const isRequestCancellation = (error: unknown): boolean =>
    error instanceof Error && ['AbortError', 'CanceledError'].includes(error.name);

interface ChatDashboardPageProps {
    embedded?: boolean;
}

const ChatDashboardPage: React.FC<ChatDashboardPageProps> = ({ embedded = false }) => {
    const [sessions, setSessions] = useState<Session[]>([]);
    const [selectedSession, setSelectedSession] = useState<Session | null>(null);
    const [isLoadingSessions, setIsLoadingSessions] = useState<boolean>(true);
    const [sessionStateSynchronized, setSessionStateSynchronized] = useState<boolean>(false);
    const selectedSessionRef = useRef<Session | null>(null);
    const reconciliationActiveRef = useRef(false);
    const reconciliationEventsRef = useRef<SessionSocketReplayUpdate[]>([]);
    const reconciliationOverflowRef = useRef(false);
    const reconciliationAbortRef = useRef<AbortController | null>(null);
    const requestSessionReconciliationRef = useRef<(() => void) | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        selectedSessionRef.current = selectedSession;
    }, [selectedSession]);

    const handleSelectSession = useCallback(async (session: Session) => {
        logger.info('ChatDashboardPage: session selected');
        selectedSessionRef.current = session;
        setSelectedSession(session);
        try {
            logger.debug('ChatDashboardPage: marking selected session as viewed');
            await markSessionAsViewed(session.workerId);
        } catch (error) {
            logger.error(
                `ChatDashboardPage: mark-viewed failed errorType=${diagnosticErrorType(error)}`,
            );
        }
    }, []);

    const recordReconciliationEvent = useCallback((update: SessionSocketReplayUpdate) => {
        if (!reconciliationActiveRef.current) return;
        if (reconciliationEventsRef.current.length >= SESSION_RECONCILIATION_EVENT_LIMIT) {
            reconciliationOverflowRef.current = true;
            return;
        }
        reconciliationEventsRef.current.push(update);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const workerIdFromUrl = params.get('workerId');
        const sessionIdFromUrl = params.get('sessionId');
        const targetId = workerIdFromUrl || sessionIdFromUrl;

        if (targetId && sessions.length > 0) {
            const sessionToSelect = sessions.find(s => s.workerId === targetId || String(s.id) === targetId);
            if (sessionToSelect && (!selectedSession || selectedSession.workerId !== sessionToSelect.workerId)) {
                logger.info('ChatDashboardPage: selecting session from a validated URL reference');
                handleSelectSession(sessionToSelect);
                params.delete('workerId');
                params.delete('sessionId');
                const remainingSearch = params.toString();
                navigate(`${location.pathname}${remainingSearch ? `?${remainingSearch}` : ''}`, { replace: true });
            }
        }
    }, [location.pathname, location.search, sessions, selectedSession, navigate, handleSelectSession]);

    const handleSessionListEvent = useCallback((sessionEventData: Partial<Session>) => {
        const eventId = ++listUpdateEventCounter;
        logger.debug(`Socket: sessionListUpdate #${eventId} received`);

        if (!sessionEventData.workerId && !sessionEventData.id) {
            logger.warn(`Socket: sessionListUpdate #${eventId} missing its authoritative identity`);
            return;
        }
        recordReconciliationEvent({
            payload: sessionEventData,
            allowCreate: true,
        });
        
        setSessions((prevSessions: Session[]) => {
            logger.debug(`Socket: sessionListUpdate #${eventId} - setSessions updater running. Prev session count: ${prevSessions.length}`);
            const existingSessionIndex = prevSessions.findIndex(s => 
                (sessionEventData.id && s.id === sessionEventData.id) || 
                (sessionEventData.workerId && s.workerId === sessionEventData.workerId)
            );

            if (existingSessionIndex > -1) {
                const merged = mergeSessionListSocketUpdate(
                    prevSessions[existingSessionIndex],
                    sessionEventData,
                );
                if (merged.authorityDecision === 'REJECT') {
                    logger.warn(`Socket: sessionListUpdate #${eventId} rejected stale or malformed authority fields`);
                }
                if (merged.requiresReconciliation) {
                    requestSessionReconciliationRef.current?.();
                }
                const updatedSessions = [...prevSessions];
                updatedSessions[existingSessionIndex] = merged.session;
                logger.debug(`Socket: sessionListUpdate #${eventId} - Updated existing session in list. New session count: ${updatedSessions.length}`);
                return updatedSessions;
            } else if (
                sessionEventData.id &&
                sessionEventData.workerId &&
                sessionEventData.language &&
                sessionEventData.channel &&
                sessionEventData.createdAt &&
                sessionEventData.updatedAt &&
                sessionEventData.publicShareId &&
                typeof sessionEventData.aiPaused === 'boolean' &&
                typeof sessionEventData.adminIntervention === 'boolean' &&
                readSessionAuthorityVersion(sessionEventData) &&
                hasValidSessionAuthoritySnapshot(sessionEventData)
            ) { 
                const newSession: Session = {
                    id: sessionEventData.id,
                    workerId: sessionEventData.workerId,
                    publicShareId: sessionEventData.publicShareId,
                    language: sessionEventData.language as Language, 
                    channel: sessionEventData.channel as CommunicationChannel, 
                    answers: sessionEventData.answers || {}, 
                    createdAt: sessionEventData.createdAt, 
                    updatedAt: sessionEventData.updatedAt, 
                    aiPaused: sessionEventData.aiPaused,
                    adminIntervention: sessionEventData.adminIntervention,
                    stateVersion: sessionEventData.stateVersion as string,
                    takeoverEpoch: sessionEventData.takeoverEpoch as string,
                    currentQuestionId: sessionEventData.currentQuestionId,
                    professionKey: sessionEventData.professionKey as ProfessionType | undefined,
                    currentQuestionType: sessionEventData.currentQuestionType as QuestionType | undefined,
                    lastMessage: sessionEventData.lastMessage,
                    lastMessageSender: sessionEventData.lastMessageSender as MessageSenderType | undefined,
                    lastMessageAt: sessionEventData.lastMessageAt,
                    lastInboundAt: sessionEventData.lastInboundAt ?? null,
                    adminLastViewedAt: sessionEventData.adminLastViewedAt || null,
                    phoneNumber: sessionEventData.phoneNumber,
                };
                if (!isValidSessionHttpSnapshot(newSession)) {
                    logger.warn(
                        `Socket: sessionListUpdate #${eventId} rejected a malformed new-session snapshot`,
                    );
                    return prevSessions;
                }
                const newState = [...prevSessions, newSession];
                logger.debug(`Socket: sessionListUpdate #${eventId} - Added new session to list. New session count: ${newState.length}`);
                return newState; 
            }
            logger.debug(`Socket: sessionListUpdate #${eventId} - No change to session list based on event. Returning prevSessions with count: ${prevSessions.length}`);
            return prevSessions; 
        });

        setSelectedSession((prevSelected: Session | null) => {
            logger.debug(`Socket: sessionListUpdate #${eventId} updating selected-session state`);
            if (prevSelected && 
                ((sessionEventData.id && prevSelected.id === sessionEventData.id) || 
                 (sessionEventData.workerId && prevSelected.workerId === sessionEventData.workerId)))
            {
                const merged = mergeSessionListSocketUpdate(prevSelected, sessionEventData);
                if (merged.authorityDecision === 'REJECT') {
                    logger.warn(`Socket: sessionListUpdate #${eventId} rejected selected-session authority fields`);
                }
                if (merged.requiresReconciliation) {
                    requestSessionReconciliationRef.current?.();
                }
                logger.debug(`Socket: sessionListUpdate #${eventId} updated the selected session`);
                return merged.session;
            }
            logger.debug(`Socket: sessionListUpdate #${eventId} - No change to selected session based on event.`);
            return prevSelected;
        });
    }, [recordReconciliationEvent]);

    const handleSessionDetailEvent = useCallback((sessionEventData: Partial<Session>) => {
        const eventId = ++listUpdateEventCounter;
        if (!sessionEventData.workerId && !sessionEventData.id) {
            logger.warn(`Socket: sessionDetailUpdate #${eventId} missing its authoritative identity`);
            return;
        }
        recordReconciliationEvent({
            payload: sessionEventData,
            allowCreate: false,
        });
        setSessions((current) => {
            const index = current.findIndex((session) =>
                (sessionEventData.id !== undefined && session.id === sessionEventData.id) ||
                (sessionEventData.workerId !== undefined && session.workerId === sessionEventData.workerId));
            if (index < 0) return current;
            const merged = mergeSessionListSocketUpdate(current[index], sessionEventData);
            if (merged.requiresReconciliation) {
                requestSessionReconciliationRef.current?.();
            }
            const next = [...current];
            next[index] = merged.session;
            return next;
        });
        setSelectedSession((current) => {
            if (
                !current ||
                !(
                    (sessionEventData.id && current.id === sessionEventData.id) ||
                    (sessionEventData.workerId && current.workerId === sessionEventData.workerId)
                )
            ) {
                return current;
            }
            const merged = mergeSessionSocketUpdate(current, sessionEventData);
            if (merged.authorityDecision === 'REJECT') {
                logger.warn(`Socket: sessionDetailUpdate #${eventId} rejected stale or malformed authority fields`);
            }
            if (merged.requiresReconciliation) {
                requestSessionReconciliationRef.current?.();
            }
            return merged.session;
        });
    }, [recordReconciliationEvent]);

    useEffect(() => {
        setSessions([]);
        setSelectedSession(null);
        setSessionStateSynchronized(false);

        const socket = socketService.connect();
        logger.info("ChatDashboardPage mounted, trying to connect socket.");
        let active = true;
        let reconciliationGeneration = 0;
        let reconciliationQueued = false;
        let reconciliationFollowupNeeded = false;

        const reconcileAuthoritativeSessions = async (): Promise<void> => {
            const generation = ++reconciliationGeneration;
            reconciliationAbortRef.current?.abort();
            const abortController = new AbortController();
            reconciliationAbortRef.current = abortController;
            reconciliationActiveRef.current = true;
            reconciliationEventsRef.current = [];
            reconciliationOverflowRef.current = false;
            setSessionStateSynchronized(false);
            setIsLoadingSessions(true);
            const selectedWorkerId = selectedSessionRef.current?.workerId;
            const timeout = window.setTimeout(
                () => abortController.abort(),
                SESSION_RECONCILIATION_TIMEOUT_MS,
            );
            try {
                const [fetchedSessions, fetchedSelectedSession] = await Promise.all([
                    getSessions(abortController.signal),
                    selectedWorkerId
                        ? getSessionByWorkerId(selectedWorkerId, abortController.signal)
                        : Promise.resolve(null),
                ]);
                if (!active || generation !== reconciliationGeneration) return;
                if (
                    !Array.isArray(fetchedSessions) ||
                    !fetchedSessions.every(isValidSessionHttpSnapshot)
                ) {
                    logger.warn(
                        'ChatDashboardPage: HTTP session list contained a malformed snapshot',
                    );
                    return;
                }
                if (reconciliationOverflowRef.current) {
                    logger.warn(
                        'ChatDashboardPage: socket replay limit reached; refreshing one authoritative snapshot',
                    );
                    void reconcileAuthoritativeSessions();
                    return;
                }
                const reconciledSessions = replaySessionSocketUpdates(
                    fetchedSessions,
                    reconciliationEventsRef.current,
                );
                setSessions(reconciledSessions);
                if (selectedSessionRef.current?.workerId !== selectedWorkerId) {
                    void reconcileAuthoritativeSessions();
                    return;
                }
                if (selectedWorkerId && selectedSessionRef.current?.workerId === selectedWorkerId) {
                    if (
                        fetchedSelectedSession &&
                        (
                            fetchedSelectedSession.workerId !== selectedWorkerId ||
                            !isValidSessionHttpSnapshot(fetchedSelectedSession)
                        )
                    ) {
                        logger.warn('ChatDashboardPage: selected authority snapshot was malformed');
                        setSessionStateSynchronized(false);
                        return;
                    }
                    const selectedFromList = fetchedSessions.find(
                        (item) => item.workerId === selectedWorkerId,
                    ) ?? null;
                    const selectedSnapshot = fetchedSelectedSession && selectedFromList
                        ? mergeSessionListSocketUpdate(
                            fetchedSelectedSession,
                            selectedFromList,
                        ).session
                        : fetchedSelectedSession ?? selectedFromList;
                    const authoritativeSelected = selectedSnapshot
                        ? replaySessionSocketUpdates(
                            [selectedSnapshot],
                            reconciliationEventsRef.current,
                        )[0] ?? null
                        : reconciledSessions.find(
                            (item) => item.workerId === selectedWorkerId,
                        ) ?? null;
                    selectedSessionRef.current = authoritativeSelected;
                    setSelectedSession(authoritativeSelected);
                }
                setSessionStateSynchronized(true);
                logger.info(`ChatDashboardPage: reconciled sessionCount=${fetchedSessions.length}`);
            } catch (err) {
                if (isRequestCancellation(err)) {
                    if (active && generation === reconciliationGeneration) {
                        logger.warn('ChatDashboardPage: session reconciliation timed out');
                    }
                } else {
                    logger.error(
                        `ChatDashboardPage: session reconciliation failed errorType=${diagnosticErrorType(err)}`,
                    );
                }
            } finally {
                window.clearTimeout(timeout);
                if (active && generation === reconciliationGeneration) {
                    if (reconciliationAbortRef.current === abortController) {
                        reconciliationAbortRef.current = null;
                    }
                    reconciliationActiveRef.current = false;
                    reconciliationEventsRef.current = [];
                    setIsLoadingSessions(false);
                    if (reconciliationFollowupNeeded) {
                        reconciliationFollowupNeeded = false;
                        requestSessionReconciliation();
                    }
                }
            }
        };

        const requestSessionReconciliation = () => {
            if (!active) return;
            if (reconciliationActiveRef.current) {
                reconciliationFollowupNeeded = true;
                return;
            }
            if (reconciliationQueued) return;
            reconciliationQueued = true;
            queueMicrotask(() => {
                reconciliationQueued = false;
                if (active) void reconcileAuthoritativeSessions();
            });
        };
        requestSessionReconciliationRef.current = requestSessionReconciliation;

        void reconcileAuthoritativeSessions();

        // ChatConversation fires this after the admin picks an attribution
        // candidate. Re-fetch the session list so the sidebar chip flips
        // from no-attribution to "Manual" without waiting for a socket push.
        const handleAttributionChanged = () => {
            void reconcileAuthoritativeSessions();
        };
        window.addEventListener('session-attribution-changed', handleAttributionChanged);

        const handleAuthenticatedConnect = () => {
            logger.info("ChatDashboardPage: Authenticated socket connected; reconciling authoritative sessions.");
            void reconcileAuthoritativeSessions();
        };

        socketService.onSessionListUpdate(handleSessionListEvent);
        socketService.onSessionDetailUpdate(handleSessionDetailEvent);
        socket.on('connect', handleAuthenticatedConnect);

        return () => {
            logger.info("ChatDashboardPage unmounting, cleaning up listeners and disconnecting socket.");
            active = false;
            reconciliationAbortRef.current?.abort();
            reconciliationAbortRef.current = null;
            if (requestSessionReconciliationRef.current === requestSessionReconciliation) {
                requestSessionReconciliationRef.current = null;
            }
            socket.off('connect', handleAuthenticatedConnect);
            socketService.offSessionListUpdate(handleSessionListEvent);
            socketService.offSessionDetailUpdate(handleSessionDetailEvent);
            window.removeEventListener('session-attribution-changed', handleAttributionChanged);
            // The embedded (AiDebugPage tab) mount does not own the shared socket singleton;
            // only the standalone /chat route disconnects it.
            if (!embedded) {
                socketService.disconnect();
            }
        };
    }, [embedded, handleSessionDetailEvent, handleSessionListEvent]);

    const handleBackToSessions = () => {
        selectedSessionRef.current = null;
        setSelectedSession(null);
    };

    return (
        <Box sx={{ 
            display: 'flex',
            width: '100%',
            height: '100%',
            backgroundColor: theme.palette.background.default,
            overflow: 'hidden'
        }}>
            {isMobile ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {selectedSession ? (
                        <ChatConversation 
                            session={selectedSession} 
                            onBack={handleBackToSessions}
                            isMobile={isMobile}
                            embedded={embedded}
                            sessionStateSynchronized={sessionStateSynchronized}
                        />
                    ) : (
                        <ChatSidebar
                            sessions={sessions}
                            selectedSession={selectedSession}
                            onSelectSession={handleSelectSession}
                            isLoading={isLoadingSessions}
                            isMobile={isMobile}
                        />
                    )}
                </Box>
            ) : (
                <>
                    <ChatSidebar
                        sessions={sessions}
                        selectedSession={selectedSession}
                        onSelectSession={handleSelectSession}
                        isLoading={isLoadingSessions}
                        isMobile={isMobile}
                    />
                    {selectedSession ? (
                        <ChatConversation
                            session={selectedSession}
                            isMobile={isMobile}
                            embedded={embedded}
                            sessionStateSynchronized={sessionStateSynchronized}
                        />
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            fontSize: '1.2em',
                            color: theme.palette.text.secondary
                        }}>
                            Select a conversation to start chatting.
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ChatDashboardPage; 
